// Single entry point for every HR & Payroll API call (DMSAPI: Controllers/Hr).
// The endpoint list below is the backend contract (details in docs/HR_Payroll_Module.md).
import { useSelector } from "react-redux";
import { _api } from "../../api/Api";

// Readable messages for transport errors (_api passes axios' text, e.g. "Request failed with status code 401")
const friendly = (res) => {
    if (!res) return { isSuccess: false, message: "No response from the server. Please try again." };
    if (res.isSuccess) return res;
    const msg = String(res.message || "");
    if (/status code 401/.test(msg)) return { ...res, message: "Your session has expired. Please log in again." };
    if (/status code 403/.test(msg)) return { ...res, message: "You do not have permission for this action." };
    if (/status code 404/.test(msg)) return { ...res, message: "This HR service is not available on the server. Check that the API is up to date." };
    if (/status code 5\d\d/.test(msg)) return { ...res, message: "The server ran into a problem. Please try again in a moment." };
    if (/Network Error|timeout/i.test(msg)) return { ...res, message: "Cannot reach the server. Check your connection and try again." };
    return res;
};

const request = (method, url, payload = {}) => new Promise((resolve) => {
    _api(method, url, method === "GET" ? null : payload, (res) => resolve(friendly(res)));
});

const qs = (obj = {}) => {
    const p = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p.append(k, v); });
    return p.toString();
};

// Setup masters: entity = designation | grade | leaveType | payComponent | taxSlab
// Each dealer has its own setup. dealerId is used for head office users (0 = template for new dealers); dealer users always get their own.
const setup = (entity) => ({
    getAll: (dealerId) => request("POST", `HrSetup/${entity}/getall?${qs({ dealerId })}`, {}),
    save: (row, dealerId) => request("POST", `HrSetup/${entity}/save?${qs({ dealerId })}`, row),
    remove: (id, dealerId) => request("POST", `HrSetup/${entity}/delete?${qs({ dealerId })}`, { id }),
});

export const hrApi = {
    departments: () => request("GET", "Department/GetAllDepartment"),

    designations: setup("designation"),
    grades: setup("grade"),
    leaveTypes: setup("leaveType"),
    payComponents: setup("payComponent"),
    taxSlabs: setup("taxSlab"),
    getSettings: (dealerId) => request("GET", `HrSetup/settings?${qs({ dealerId })}`),
    saveSettings: (settings, dealerId) => request("POST", `HrSetup/settings/save?${qs({ dealerId })}`, settings),
    holidays: (year) => request("GET", `HrSetup/holidays?${qs({ year })}`),

    // Employees
    employees: (filters = {}) => request("POST", "HrEmployee/getall", filters),
    employee: (employeeId) => request("GET", `HrEmployee/get?${qs({ employeeId })}`),
    saveEmployee: (data) => request("POST", "HrEmployee/save", data),
    changeEmployeeStatus: (data) => request("POST", "HrEmployee/change-status", data),
    createEmployeeLogin: (data) => request("POST", "HrEmployee/create-login", data),
    employeesWithoutLogin: (dealerId) => request("GET", `HrEmployee/without-login?${qs({ dealerId })}`),

    // Salary structure
    salaryStructure: (employeeId) => request("GET", `HrPayroll/salary-structure?${qs({ employeeId })}`),
    saveSalaryStructure: (data) => request("POST", "HrPayroll/salary-structure/save", data),

    // Attendance
    dailyAttendance: (filters) => request("POST", "HrAttendance/daily", filters),
    saveDailyAttendance: (data) => request("POST", "HrAttendance/daily/save", data),
    importAttendance: (rows) => request("POST", "HrAttendance/import", { rows }),
    monthlyAttendance: (filters) => request("POST", "HrAttendance/monthly", filters),

    // Leave
    leaveRequests: (filters) => request("POST", "HrLeave/requests", filters),
    saveLeaveRequest: (data) => request("POST", "HrLeave/request/save", data),
    leaveAction: (data) => request("POST", "HrLeave/request/action", data),
    leaveBalances: (filters) => request("POST", "HrLeave/balances", filters),

    // Loans & advances
    loans: (filters) => request("POST", "HrPayroll/loans/getall", filters),
    saveLoan: (data) => request("POST", "HrPayroll/loans/save", data),
    cancelLoan: (id) => request("POST", "HrPayroll/loans/cancel", { id }),
    loanInstallments: (loanId) => request("GET", `HrPayroll/loans/installments?${qs({ loanId })}`),

    // Incentives
    incentives: (filters) => request("POST", "HrPayroll/incentives/getall", filters),
    calculateIncentives: (data) => request("POST", "HrPayroll/incentives/calculate", data),
    saveIncentive: (data) => request("POST", "HrPayroll/incentives/save", data),
    incentiveAction: (data) => request("POST", "HrPayroll/incentives/action", data),
    deleteIncentive: (id) => request("POST", "HrPayroll/incentives/delete", { id }),

    // Payroll runs
    payrollRuns: (filters) => request("POST", "HrPayroll/runs/getall", filters),
    generatePayroll: (data) => request("POST", "HrPayroll/runs/generate", data),
    payrollRun: (runId) => request("GET", `HrPayroll/runs/get?${qs({ runId })}`),
    recalculatePayroll: (runId) => request("POST", "HrPayroll/runs/recalculate", { runId }),
    adjustPayroll: (data) => request("POST", "HrPayroll/runs/adjust", data),
    payrollAction: (data) => request("POST", "HrPayroll/runs/action", data),
    // Finance vouchers of the run: preview with the dealer's mapped accounts, posted vouchers, last posting error
    payrollJournal: (runId) => request("GET", `HrPayroll/runs/journal?${qs({ runId })}`),
    deletePayroll: (runId) => request("POST", "HrPayroll/runs/delete", { runId }),
    payslips: (filters) => request("POST", "HrPayroll/payslips", filters),

    // Final settlement
    settlementPreview: (data) => request("POST", "HrPayroll/final-settlement/preview", data),
    saveSettlement: (data) => request("POST", "HrPayroll/final-settlement/save", data),
    settlements: (filters) => request("POST", "HrPayroll/final-settlement/getall", filters),
    paySettlement: (id) => request("POST", "HrPayroll/final-settlement/pay", { id }),

    // Reports: headcount | attendance-summary | leave-balance | payroll-register | tax-deduction | eobi | bank-transfer | salary-comparison | joiners-leavers
    report: (type, filters) => request("POST", `HrReport/${type}`, filters),
    dashboard: (filters) => request("POST", "HrReport/dashboard", filters),
};

// Dealer scope used by every HR screen: dealer users are locked to their dealer, distributor users pick one.
export const useHrScope = () => {
    const user = useSelector((state) => state.user) || {};
    const reduxDealers = useSelector((state) => state.dealers) || [];
    const dealers = reduxDealers;
    const isDistributor = !user?.dealerId;
    return { user, dealers, isDistributor, defaultDealerId: user?.dealerId || 0 };
};
